import { Capacitor } from "@capacitor/core";
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from "@capacitor/push-notifications";
import { isIOS } from "@/lib/platform";
import { NotificationTransformer } from "../shared/notification-transformer";
import type {
  DeviceIdGenerator,
  NotificationHandler,
  PermissionStatus,
  PushNotificationService,
  TokenManager,
} from "../types";

export class CapacitorPushService implements PushNotificationService {
  private currentToken: string | null = null;

  constructor(
    private tokenManager: TokenManager,
    private notificationHandler: NotificationHandler,
    private deviceIdGenerator: DeviceIdGenerator
  ) {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    if (!this.isSupported()) return;

    this.setupListeners();
  }

  private setupListeners(): void {
    PushNotifications.addListener("registration", (token: Token) => {
      this.currentToken = token.value;
      this.handleTokenRegistration(token.value);
    });

    PushNotifications.addListener("registrationError", (error: any) => {
      console.error("Push registration error:", error);
    });

    PushNotifications.addListener(
      "pushNotificationReceived",
      (notification: PushNotificationSchema) => {
        const userNotification = NotificationTransformer.transform(
          {
            id: notification.id,
            title: notification.title,
            body: notification.body,
            data: notification.data,
          },
          "capacitor"
        );
        this.notificationHandler.handleNotification(userNotification);
      }
    );

    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification: ActionPerformed) => {
        this.handleNotificationAction(notification);
      }
    );
  }

  async requestPermission(): Promise<boolean> {
    const permissionResult = await PushNotifications.requestPermissions();
    if (permissionResult.receive !== "granted") return false;

    await PushNotifications.register();
    return true;
  }

  async getToken(): Promise<string | null> {
    return this.currentToken;
  }

  getCurrentToken(): string | null {
    return this.currentToken;
  }

  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  async getPermissionStatus(): Promise<PermissionStatus> {
    const status = await PushNotifications.checkPermissions();
    return status.receive as PermissionStatus;
  }

  async onUserLogin(): Promise<void> {
    const permissionStatus = await this.getPermissionStatus();
    if (permissionStatus === "granted") {
      await PushNotifications.register();
    }
  }

  async onUserLogout(): Promise<void> {
    if (this.currentToken) {
      await this.tokenManager.unregisterToken(this.currentToken);
      this.currentToken = null;
    }
  }

  async onAccountSwitch(): Promise<void> {
    if (this.currentToken) {
      const platform = isIOS() ? "ios" : "android";
      const deviceId = await this.deviceIdGenerator.generate();
      await this.tokenManager.registerToken(this.currentToken, platform, deviceId);
    } else {
      await this.onUserLogin();
    }
  }

  async removeAllDeliveredNotifications(): Promise<void> {
    await PushNotifications.removeAllDeliveredNotifications();
  }

  async getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  }

  private async handleTokenRegistration(token: string): Promise<void> {
    const platform = isIOS() ? "ios" : "android";
    const deviceId = await this.deviceIdGenerator.generate();
    await this.tokenManager.registerToken(token, platform, deviceId);
  }

  private handleNotificationAction(actionPerformed: ActionPerformed): void {
    const notification = actionPerformed.notification;
    const data = notification.data;

    if (notification.id) {
    }

    if (data?.actionUrl && data.actionUrl !== "/") {
      this.notificationHandler.triggerNavigationEvent(data.actionUrl, notification.id || "", data);
    }
  }
}
